import axios from "axios";
import raw from "raw.macro";
import { useEffect, useState } from "react";
import React from "react";
import ReactMarkdown from "react-markdown";
import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";

import Breadcrumbs from "../component/breadcrumbs";
import CustomHelmet from "../component/CustomHelmet";
import FullScreenLoader from "../component/FullScreenLoader";
import Intro from "../component/Intro";
import BaseContainer from "../component/layout/base-container";
import { fetcher } from "../lib/fetcher";
import { components } from "../lib/markdownComponentMapping";

const types = {
  S: {
    infoString: "Schüler*in",
    infoText: raw("../textblocke/schulerinnen/info.md"),
    question_label: "student_question",
    finalText: raw("../textblocke/schulerinnen/abschluss.md"),
  },
  L: {
    infoString: "Lehrperson",
    infoText: raw("../textblocke/lehrerinnen/info.md"),
    question_label: "teacher_question",
    finalText: raw("../textblocke/lehrerinnen/abschluss.md"),
  },
  E: {
    infoString: "Beobachter*in",
    infoText: raw("../textblocke/expertinnen/info.md"),
    question_label: "expert_question",
    finalText: raw("../textblocke/expertinnen/abschluss.md"),
  },
};

const feedback_values = [1, 2, 3, 4, 99];
const feedback_strings = [
  "Stimmt nicht",
  "Stimmt eher nicht",
  "Stimmt eher",
  "Stimmt genau",
  "Nicht beurteilbar",
];

export default function Survey() {
  const navigate = useNavigate();

  const params = useParams();
  const tan = params.tan;

  const {
    data: survey,
    error: survey_error,
    mutate: mutate_survey,
  } = useSWR(`${process.env.REACT_APP_BASE_URL}survey/${tan}/`, fetcher);

  const { data: questions, error: qustions_error } = useSWR(
    `${process.env.REACT_APP_BASE_URL}question/`,
    fetcher
  );

  const [values, setValues] = useState();

  const updateValues = (value, questionsID, answerURL) => {
    // update local values
    setValues((prevState) => {
      const updated = prevState.slice();
      updated.find((answer) => answer.questionsID === questionsID)["value"] =
        value;
      return updated;
    });

    // Send result
    const data = value === 99 ? { not_assessed: true } : { score: value };
    axios.patch(answerURL, data).then(() => mutate_survey());
  };

  useEffect(() => {
    if (survey && survey.answers) {
      setValues(
        survey.answers.map((answer) => ({
          questionsID: answer.question,
          value: answer.score,
        }))
      );
    }
  }, [survey]);

  if (survey_error) return <div>failed to load survey</div>;
  if (qustions_error) return <div>failed to load questions</div>;
  if (!survey || !questions) return <FullScreenLoader />;

  return (
    <>
      <CustomHelmet pageTitle={`Umfrage ${types[survey.type].infoString}`} />
      <Breadcrumbs
        listItems={[
          { name: "Home", url: "/" },
          { name: "Umfrage", url: "/umfrage" },
          { name: tan, url: `/umfrage/${tan}` },
        ]}
      />
      <BaseContainer>
        <Intro title={"Umfrage"}>
          <p>
            Umfrage für {types[survey.type].infoString}: {tan}
          </p>
        </Intro>
        <div>
          {!survey.able_to_fill_out ? (
            <>
              <p>
                Diese Umfrage ist abgeschlossen und kann deshalb nicht
                ausgefüllt werden.
              </p>
              <Link
                to="/umfrage"
                className="Button color-primary mb-8"
                style={{ marginTop: "1rem" }}
              >
                Zurück
              </Link>
            </>
          ) : (
            <>
              <ReactMarkdown components={components}>
                {types[survey.type].infoText}
              </ReactMarkdown>
              <ul className="NewsList--list">
                {survey.answers.map((answer) => (
                  <li key={answer.url}>
                    <div className="NewsListItem">
                      <div
                        className="NewsListItem--content"
                        style={{ width: "100%" }}
                      >
                        <p className="NewsArticleItem--title">
                          {questions &&
                            questions.results.find(
                              (question) =>
                                question["id"] === answer["question"]
                            )[types[survey.type]["question_label"]]}
                        </p>
                        <div
                          className="flex justify-between"
                          style={{
                            width: "100%",
                          }}
                        >
                          {feedback_values.map((value) => (
                            <div
                              className="l-1of4"
                              key={value}
                              style={{
                                textAlign: "center",
                                padding: "0.1rem",
                                display: "inherit",
                              }}
                            >
                              <button
                                style={{
                                  whiteSpace: "normal",
                                  paddingLeft: "0.5rem",
                                  paddingRight: "0.5rem",
                                }}
                                className={`Button ${
                                  typeof values !== "undefined" &&
                                  values.find(
                                    (value) =>
                                      value.questionsID === answer["question"]
                                  )["value"] === value &&
                                  "color-primary"
                                }`}
                                onClick={() =>
                                  updateValues(
                                    value,
                                    answer["question"],
                                    answer.url
                                  )
                                }
                              >
                                <span className="Button--inner">
                                  {
                                    feedback_strings[
                                      feedback_values.indexOf(value)
                                    ]
                                  }
                                </span>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="FormButtons mb-8">
                <button
                  disabled={
                    typeof values === "undefined" ||
                    values.some((v) => v.value == null)
                  }
                  className="Button color-primary size-large"
                  style={{
                    marginRight: "unset",
                    marginLeft: "auto",
                    ...(typeof values === "undefined" ||
                      (values.some((v) => v.value == null) && {
                        backgroundColor: "rgba(var(--c-grey-light),1)",
                        color: "rgba(var(--c-black),0.6)",
                      })),
                  }}
                  onClick={async () => {
                    //Submit all answers again
                    for (let i = 0; i < values.length; i++) {
                      const value = values[i];
                      const answer = survey["answers"].find(
                        (a) => a["question"] === value["questionsID"]
                      );
                      await axios.patch(answer.url, { score: value.value });
                    }

                    // Set survey to done
                    await axios.patch(
                      `${process.env.REACT_APP_BASE_URL}survey/${tan}/`,
                      {
                        done: true,
                      }
                    );
                    navigate("/danke");
                  }}
                >
                  <span className="Button--inner">Antworten absenden</span>
                </button>
              </div>
            </>
          )}
        </div>
      </BaseContainer>
    </>
  );
}
